/* eslint-disable react/no-multi-comp */
import React, { forwardRef } from 'react';
import { Mention, MentionProps, MentionsInput, MentionsInputProps } from 'react-mentions';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { CaptionWrapperStyle, ValidationResult } from '@/components/composite/Caption/types';

import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import Tooltip from '@/components/base/Tooltip';

import Popover from './Popover';

type TextAreaStyleType = 'default' | 'input';

const captionStyle: Record<CaptionWrapperStyle, string> = {
    default: 'px-8 pt-8 pb-5',
    crossPost: 'px-5 pt-5 pb-3.5',
    contentInspiration: 'p-0',
    firstComment: 'px-0 pt-0 pb-4 !space-y-2',
    firstCommentBorder: 'px-4 pt-4 pb-2 !space-y-2',
    media_library: 'bg-white rounded-md px-8 pt-8 pb-5 space-y-4 dark:bg-icoDarkMode-deepBlack'
};

const textAreaStyle: Record<TextAreaStyleType, string> = {
    default:
        'font-GMedium bg-transparent p-0 border-0 placeholder:text-sm focus:outline-0 focus:ring-0 peer',
    input: 'border border-icoGray-300 dark:border-icoDarkMode-beton py-5 px-8 rounded-md w-full bg-transparent focus:ring-2 focus:ring-icoBlue-200 placeholder:font-GMedium'
};

// ==========================================
// Main Caption Component
// ==========================================

interface CaptionProps {
    children?: React.ReactNode;
    captionWrapperStyle?: CaptionWrapperStyle;
    className?: string;
    [key: string]: unknown;
}

const CaptionRoot = forwardRef<HTMLDivElement, CaptionProps>(
    ({ children, captionWrapperStyle = 'default', className, ...restProps }, ref) => {
        return (
            <div
                className={clsx(
                    'relative flex flex-col',
                    captionStyle[captionWrapperStyle as CaptionWrapperStyle],
                    className
                )}
                ref={ref}>
                {
                    React.Children.map(children, (child) => {
                        if (child && React.isValidElement(child)) {
                            const newChild = React.cloneElement(child, {
                                ...((child.props as Record<string, unknown>) ?? {}),
                                ...restProps
                            } as React.ComponentProps<any>);
                            return newChild;
                        }
                        return child;
                    }) as React.ReactNode
                }
            </div>
        );
    }
);

CaptionRoot.displayName = 'Caption';

// ==========================================
// Sub Components
// ==========================================

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'> {
    style?: TextAreaStyleType;
    className?: string;
}

const CaptionTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({ className, style = 'default', ...restProps }, ref) => (
        <textarea
            ref={ref}
            className={clsx(
                'scrollbar z-1 resize-none text-sm placeholder:text-icoGray-400 placeholder:dark:text-icoDarkMode-wolf',
                textAreaStyle[style],
                className
            )}
            {...restProps}
        />
    )
);

CaptionTextArea.displayName = 'CaptionTextArea';

const CaptionMentionsWrapper = forwardRef<HTMLTextAreaElement, MentionsInputProps>(
    (props, inputRef) => (
        <MentionsInput inputRef={inputRef} suggestionsPortalHost={document.body} {...props} />
    )
);

CaptionMentionsWrapper.displayName = 'CaptionMentionsWrapper';

const CaptionMention = (props: MentionProps) => <Mention {...props} />;

interface CaptionBorderProps {
    className?: string;
}

const CaptionBorder = ({ className }: CaptionBorderProps) => (
    <div
        className={clsx(
            'absolute inset-0 z-0 rounded-md border-0 ring-1 transition-all',
            className
        )}
    />
);

interface CaptionMentionSuggestionProps {
    focused?: boolean;
    highlightedDisplay?: string;
    entry?: {
        link?: string;
        [key: string]: unknown;
    };
}

const CaptionMentionSuggestion = ({
    focused,
    highlightedDisplay,
    entry
}: CaptionMentionSuggestionProps) => (
    <div className="flex flex-col space-y-1.5 p-1">
        <div
            className={clsx('flex flex-col rounded-m px-2 py-2 text-sm', {
                'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20': focused
            })}>
            {highlightedDisplay}
        </div>
        <a
            className={clsx(
                'dark:text-icoDarkmode-wolf inline-flex items-center space-x-1 px-2 pb-2 text-xs text-icoGray-500 hover:text-icoBlue'
            )}
            href={entry?.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}>
            <Icon icon="link" className="text-xxs" />
            <span className="underline">{entry?.link}</span>
        </a>
    </div>
);

interface CaptionContentProps {
    children?: React.ReactNode;
    className?: string;
    [key: string]: unknown;
}

const CaptionContent = ({ children, className, ...restProps }: CaptionContentProps) => {
    return (
        <div className={clsx('flex', className)}>
            {React.Children.map(children, (child) => {
                if (child && React.isValidElement(child)) {
                    const newChild = React.cloneElement(child, {
                        ...((child.props ?? {}) as React.Attributes),
                        ...restProps
                    } as Record<string, unknown>);
                    return newChild;
                }
                return child;
            })}
        </div>
    );
};

interface CaptionLoadingProps {
    requestStatus?: string;
    captionWrapperStyle?: CaptionWrapperStyle;
}

const CaptionLoading = ({ requestStatus, captionWrapperStyle }: CaptionLoadingProps) => {
    if (requestStatus !== 'pending') {
        return null;
    }
    return (
        <div
            className={clsx('absolute', {
                'bottom-11 right-2': captionWrapperStyle === 'contentInspiration',
                'bottom-15 right-10': captionWrapperStyle !== 'contentInspiration'
            })}>
            <Loader className="h-5 w-5 text-icoBlue" />
        </div>
    );
};

interface CaptionMentionsRequestErrorProps {
    requestStatus?: string;
}

const CaptionMentionsRequestError = ({ requestStatus }: CaptionMentionsRequestErrorProps) => {
    const { t } = useTranslation('errors');
    if (requestStatus !== 'error') {
        return null;
    }
    return (
        <Tooltip content={t('error_occured_fetch_data')} placement="top-end">
            <Icon icon="warning" className="absolute bottom-15 right-8 z-10 text-xl text-icoRed" />
        </Tooltip>
    );
};

interface CaptionFooterProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
    className?: string;
}

const CaptionFooter = ({ children, className, ...restProps }: CaptionFooterProps) => {
    return (
        <div className={clsx('z-2 flex items-center', className)} {...restProps}>
            {children}
        </div>
    );
};

interface CaptionRestrictionsProps
    extends Omit<React.HTMLAttributes<HTMLParagraphElement>, 'content'> {
    valid?: ValidationResult;
    className?: string;
    content?: React.ReactNode;
}

const CaptionRestrictions = ({
    valid,
    className,
    content,
    ...restProps
}: CaptionRestrictionsProps) => {
    return (
        <p
            className={clsx('font-GMedium text-xs', className, {
                'text-icoGray-500 dark:text-icoDarkMode-wolf': valid?.valid && !valid?.warning,
                'text-icoYellow': valid?.warning,
                'text-icoRed': !valid?.valid
            })}
            {...restProps}>
            {content}
        </p>
    );
};

const Caption = Object.assign(CaptionRoot, {
    Content: CaptionContent,
    TextArea: CaptionTextArea,
    Border: CaptionBorder,
    Footer: CaptionFooter,
    Restrictions: CaptionRestrictions,
    Popover: Popover,
    MentionsWrapper: CaptionMentionsWrapper,
    Mention: CaptionMention,
    MentionSuggestion: CaptionMentionSuggestion,
    Loading: CaptionLoading,
    MentionsRequestError: CaptionMentionsRequestError
});

export { Caption };
export default Caption;

export type {
    CaptionBorderProps,
    CaptionContentProps,
    CaptionFooterProps,
    CaptionLoadingProps,
    CaptionMentionsRequestErrorProps,
    CaptionMentionSuggestionProps,
    CaptionProps,
    CaptionRestrictionsProps,
    TextAreaProps,
    TextAreaStyleType
};

// Re-export shared types
export type {
    CaptionLayoutConfig,
    CaptionRestriction,
    CaptionWrapperStyle,
    SocialProfile,
    ValidationResult
} from '@/components/composite/Caption/types';
